import React, { useState, useEffect, useRef, forwardRef } from 'react';
import { 
  PlusIcon, 
  TrashIcon, 
  PencilSquareIcon, 
  CheckIcon, 
  XMarkIcon,
  BriefcaseIcon
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { useAppData } from '../../contexts/AppDataContext';
import { getDepartments, addDepartment, updateDepartment, deleteDepartment, refreshDepartments } from '../../utils/departmentUtils';

const DepartmentManager = forwardRef((props, ref) => {
  const [departmentsList, setDepartmentsList] = useState([]);
  const [newDepartment, setNewDepartment] = useState('');
  const [editingDepartment, setEditingDepartment] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const inputRef = useRef(null);

  // Force a refresh of the component
  const forceRefresh = () => {
    setRefreshTrigger(prev => prev + 1);
    loadDepartments();
  };

  // Function to load departments
  const loadDepartments = () => {
    const departments = getDepartments();
    console.log("DepartmentManager loaded departments:", departments);
    setDepartmentsList(departments);
  };

  // Load departments on mount and when refreshTrigger changes
  useEffect(() => {
    loadDepartments();
    
    // Set up ref for adding departments
    if (ref) {
      ref.current = handleAddDepartmentAndRefresh;
    }
    
    // Listen for storage events from other components
    const handleStorageChange = () => {
      console.log("Storage event detected in DepartmentManager");
      loadDepartments();
    };
    
    window.addEventListener('storage', handleStorageChange);
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [refreshTrigger]);

  // Add a new department and refresh
  const handleAddDepartmentAndRefresh = () => {
    const trimmedDept = newDepartment.trim();
    
    if (!trimmedDept) {
      toast.error("Veuillez saisir un nom de département");
      return;
    }
    
    const success = addDepartment(trimmedDept);
    
    if (success) {
      setNewDepartment('');
      toast.success('Département ajouté avec succès');
      forceRefresh();
    } else {
      toast.error("Ce département existe déjà ou n'a pas pu être ajouté");
    }
  };

  // Start editing a department
  const startEditing = (department) => {
    setEditingDepartment(department);
    setEditValue(department);
  };

  // Cancel editing
  const cancelEditing = () => {
    setEditingDepartment(null);
    setEditValue('');
  };

  // Save department edit
  const saveEditAndRefresh = (oldDepartment) => {
    const trimmedValue = editValue.trim();
    
    if (!trimmedValue) {
      toast.error("Le nom du département ne peut pas être vide");
      return;
    }
    
    const success = updateDepartment(oldDepartment, trimmedValue);
    
    if (success) {
      setEditingDepartment(null);
      toast.success('Département mis à jour avec succès');
      forceRefresh();
    } else {
      toast.error("Ce nom de département existe déjà ou la mise à jour a échoué");
    }
  };

  // Delete a department
  const handleDeleteDepartmentAndRefresh = (department) => {
    const success = deleteDepartment(department);
    
    if (success) {
      toast.success('Département supprimé avec succès');
      forceRefresh();
    } else {
      toast.error("Impossible de supprimer ce département (il est peut-être utilisé par des employés)");
    }
  };

  // Get department label (for standard departments)
  const standardDepartments = {
    'stock': 'Stock',
    'logistics': 'Logistique',
    'purchasing': 'Achats',
    'sales': 'Ventes',
    'transport': 'Transport',
    'management': 'Direction',
    'it': 'Informatique',
    'accounting': 'Comptabilité',
    'reception': 'Accueil',
    'coaching': 'Coaching'
  };
  
  const getDepartmentLabel = (value) => {
    return standardDepartments[value] || value;
  };

  return (
    <div className="bg-white/10 backdrop-blur-lg rounded-3xl border border-white/20 p-6 space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold text-white flex items-center">
          <BriefcaseIcon className="h-6 w-6 mr-2 text-blue-400" />
          Départements
        </h2>
        <button 
          onClick={forceRefresh}
          className="text-sm text-blue-400 hover:text-blue-300"
        >
          Rafraîchir
        </button>
      </div>
      
      <div className="flex items-center space-x-2">
        <input
          ref={inputRef}
          type="text"
          value={newDepartment}
          onChange={(e) => setNewDepartment(e.target.value)}
          placeholder="Nouveau département..."
          className="flex-1 p-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-500"
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              handleAddDepartmentAndRefresh();
            }
          }}
        />
        <button
          onClick={handleAddDepartmentAndRefresh}
          className="p-3 bg-blue-500 hover:bg-blue-600 text-white rounded-xl flex items-center transition-colors"
        >
          <PlusIcon className="h-5 w-5" />
        </button>
      </div>
      
      <div className="divide-y divide-white/10">
        {departmentsList.length > 0 ? (
          departmentsList.map((department, index) => (
            <div 
              key={`${department}-${index}`} 
              className="py-3 flex items-center justify-between"
            >
              {editingDepartment === department ? (
                <div className="flex-1 flex items-center space-x-2">
                  <input
                    type="text"
                    value={editValue}
                    onChange={(e) => setEditValue(e.target.value)}
                    className="flex-1 p-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    autoFocus
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        saveEditAndRefresh(department);
                      } else if (e.key === 'Escape') {
                        cancelEditing();
                      }
                    }}
                  />
                  <button 
                    onClick={() => saveEditAndRefresh(department)}
                    className="p-2 bg-green-500/20 text-green-400 rounded-lg hover:bg-green-500/30"
                  >
                    <CheckIcon className="h-4 w-4" />
                  </button>
                  <button 
                    onClick={cancelEditing}
                    className="p-2 bg-red-500/20 text-red-400 rounded-lg hover:bg-red-500/30"
                  >
                    <XMarkIcon className="h-4 w-4" />
                  </button>
                </div>
              ) : (
                <>
                  <div className="flex items-center">
                    <span className="text-white font-medium">{getDepartmentLabel(department)}</span>
                  </div>
                  <div className="flex space-x-2">
                    <button 
                      onClick={() => startEditing(department)}
                      className="p-2 bg-yellow-500/20 text-yellow-400 rounded-lg hover:bg-yellow-500/30"
                      title="Modifier"
                    >
                      <PencilSquareIcon className="h-4 w-4" />
                    </button>
                    <button 
                      onClick={() => handleDeleteDepartmentAndRefresh(department)}
                      className="p-2 bg-red-500/20 text-red-400 rounded-lg hover:bg-red-500/30"
                      title="Supprimer"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </div>
                </>
              )}
            </div>
          ))
        ) : (
          <div className="py-4 text-center text-white/60">
            Aucun département disponible
          </div>
        )}
      </div>
    </div>
  );
});

export default DepartmentManager;